import AppContext from 'App/AppContext';
import { IPDefaultButton, IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { DialogOk } from 'components/Dialogs/DialogOk';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import SuccesMessage from 'components/Notification/SuccesMessage';
import { CardToggle, ICardToggleOption } from 'components/Utils/CardToggle';
import { getSubscriptionTypeText, sendSwitchMsgToSupport, switchTrialSubscription } from 'globalFunctions';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapLargePlus5,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import { TFunction } from 'i18next';
import { ISONorm } from 'models/norm';
import { TenantOnboarding, TrialWelcomeIntent } from 'models/tenant';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetDedupControlCount } from 'services/Api/controlService';
import { apiRequest } from 'services/Auth/authConfig';
import { SubscriptionTypes } from 'utils/subscription';

interface ITrialWelcomeFrameworksNIS2Props {
  onboardingState: TenantOnboarding;
  standards: ISONorm[];
  onNext: (state: TenantOnboarding) => void;
  onPrev: (state: TenantOnboarding) => void;
}

const TrialWelcomeFrameworksNIS2 = (props: ITrialWelcomeFrameworksNIS2Props) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'dashboard', 'adminCompany', 'adminSubscription']);
  const [selectedKeys, setSelectedKeys] = useState<string[]>(
    props.onboardingState.onboardingSteps.certifiedStandards?.map((s) => String(s)) || [],
  );
  const [dedupCount, setDedupCount] = useState<number>(0);
  const [showSwitchMsg, setShowSwitchMsg] = useState<SubscriptionTypes | undefined>(undefined);
  const [showSwitchMsgConfirm, setShowSwitchMsgConfirm] = useState<boolean>(false);

  const mostUsedFrameworksIds = [66, 67, 68];

  useEffect(() => {
    const getDepupControls = async (ids: string[]) => {
      try {
        const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
        const dedupCount = await apiGetDedupControlCount(ids, accessToken);
        setDedupCount(dedupCount);
      } catch (err) {
        appContext.setError(err);
      }
    };

    getDepupControls([...selectedKeys]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys]);

  const applyNewState = (state: TenantOnboarding): TenantOnboarding => {
    const newOnboardingState = state.clone();

    newOnboardingState.onboardingSteps.certifiedStandards = selectedKeys.map((id) => parseInt(id));
    newOnboardingState.onboardingSteps.wantsToCertify = false;

    return newOnboardingState;
  };

  const getCardOptions = (standardList: ISONorm[]): ICardToggleOption[] => {
    const options: ICardToggleOption[] = [];

    options.push(
      ...mostUsedFrameworksIds
        .filter((s) => standardList.some((s2) => s2.isoNormId === s))
        .map((m) => {
          const s = standardList.find((s) => s.isoNormId === m);

          return {
            key: `${s?.isoNormId}`,
            text: s?.name || '',
            subText: s?.shortDescription || '',
          };
        }),
    );

    //disable items for which an standard is selected that is an upgrade of that item
    options.forEach((option) => {
      const standard = standardList.find((s) => s.isoNormId.toString() === option.key);
      if (standard && standard.upgradeISONormId && !standard.allowDowngrade) {
        const upgrade = standardList.find((s) => s.isoNormId === standard.upgradeISONormId);
        if (upgrade && selectedKeys.includes(upgrade.isoNormId.toString())) {
          option.disabled = true;
        }
      }
    });

    return options;
  };

  const ansersAreNotValid = (): boolean => {
    const totalSelected = selectedKeys.length;
    if (totalSelected === 0) return true;

    return false;
  };

  const switchToPremiumTrial = async () => {
    try {
      const isSwitched = await switchTrialSubscription(appContext, SubscriptionTypes.Premium);
      if (isSwitched) {
        window.location.reload();
      } else {
        //Show a message that support will contact them to switch the subscription
        setShowSwitchMsg(SubscriptionTypes.Premium);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  //
  // Main render
  //
  return (
    <IPStack styles={{ root: { width: 900, paddingRight: 20, paddingBottom: 20 } }}>
      <IPStack horizontalAlign="center" verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPStack verticalFill tokens={globalStackTokensGapLargePlus5}>
            <IPStack tokens={globalStackTokensGapExtraSmall}>
              <IPText block variant="large" styles={globalTextStylesBold}>
                {t('dashboard:TrialWelcome.Frameworks.Title')}
              </IPText>
              <IPText block>{t('dashboard:TrialWelcome.Frameworks.SubTitle')}</IPText>
            </IPStack>
            <IPStack.Item>
              <CardToggle
                options={getCardOptions(props.standards)}
                selectedKeys={selectedKeys}
                onSelect={(keys) => setSelectedKeys(keys as string[])}
                compact={true}
                multiSelect={true}
                allowEmptySelection={true}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPStack tokens={globalStackTokensGapSmall}>
                {dedupCount > 0 && (
                  <IPStack.Item>
                    <SuccesMessage
                      message={t('dashboard:TrialWelcome.Frameworks.DedupMessage', { count: dedupCount })}
                    />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
            {props.onboardingState.onboardingSteps.intent === TrialWelcomeIntent.IsCertified && (
              <IPStack tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPText styles={globalTextStylesBold}>{t('dashboard:TrialWelcome.GoalsNIS2.SwitchInfo')}</IPText>
                </IPStack.Item>
                <IPStack.Item>
                  <IPDefaultButton
                    text={t('dashboard:TrialWelcome.GoalsNIS2.Switch')}
                    onClick={() => switchToPremiumTrial()}
                  />
                  <DialogYesNo
                    onYes={() => {
                      sendSwitchMsgToSupport(appContext, showSwitchMsg, t);
                      setShowSwitchMsg(undefined);
                      setShowSwitchMsgConfirm(true);
                    }}
                    onNo={() => setShowSwitchMsg(undefined)}
                    hidden={showSwitchMsg === undefined}
                    title={t('adminSubscription:Dialogs.SwitchSubscription.Title')}
                    subText={t('adminSubscription:Dialogs.SwitchSubscription.SubText', {
                      type: getSubscriptionTypeText(showSwitchMsg, t as TFunction<string[]>),
                    })}
                  />
                  <DialogOk
                    onOk={() => setShowSwitchMsgConfirm(false)}
                    hidden={!showSwitchMsgConfirm}
                    title={t('adminSubscription:Dialogs.SwitchSubscriptionConfirm.Title')}
                    subText={t('adminSubscription:Dialogs.SwitchSubscriptionConfirm.SubText')}
                  />
                </IPStack.Item>
              </IPStack>
            )}
            <IPStack horizontal tokens={globalStackTokensGapMedium}>
              <IPStack.Item>
                <IPDefaultButton
                  text={t('dashboard:TrialWelcome.Goals.ButtonPrev')}
                  onClick={() => props.onPrev(applyNewState(props.onboardingState))}
                />
              </IPStack.Item>
              <IPStack.Item>
                <IPPrimaryButton
                  disabled={ansersAreNotValid()}
                  text={t('dashboard:TrialWelcome.Goals.ButtonNext')}
                  onClick={() => props.onNext(applyNewState(props.onboardingState))}
                />
              </IPStack.Item>
            </IPStack>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </IPStack>
  );
};

export default TrialWelcomeFrameworksNIS2;
